import { jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef } from "react";
import styles from "./MicLevelMeter.module.scss";
const getAudioContextCtor = () => {
    if (typeof window === "undefined")
        return null;
    const w = window;
    return w.AudioContext || w.webkitAudioContext || null;
};
// Целевой шаг между столбиками (в CSS-пикселях) и их толщина.
const BAR_STEP = 9;
const BAR_WIDTH = 4;
// Доля ширины под «живые» столбики; правее — пунктирная линия.
const ACTIVE_RATIO = 0.62;
export const MicLevelMeter = ({ stream, }) => {
    const canvasRef = useRef(null);
    useEffect(() => {
        if (!stream)
            return;
        const canvas = canvasRef.current;
        const AudioCtx = getAudioContextCtor();
        if (!canvas || !AudioCtx)
            return; // Web Audio недоступен — тихий фолбэк.
        let audioCtx = null;
        let source = null;
        let analyser = null;
        let rafId = 0;
        let disposed = false;
        try {
            audioCtx = new AudioCtx();
            source = audioCtx.createMediaStreamSource(stream);
            analyser = audioCtx.createAnalyser();
            analyser.fftSize = 1024;
            analyser.smoothingTimeConstant = 0.82;
            source.connect(analyser);
        }
        catch {
            // Не удалось поднять граф — просто не рисуем.
            return;
        }
        const freqData = new Uint8Array(analyser.frequencyBinCount);
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        // Цвет берём из CSS-переменной темы, чтобы совпадать со стилем проекта.
        const brand = getComputedStyle(canvas).getPropertyValue("--vp-brand").trim() ||
            "#ff5544";
        // Сглаженные высоты столбиков (0..1); переживают кадры.
        let heights = new Float32Array(0);
        const drawRoundedBar = (x, centerY, w, barH) => {
            const r = Math.min(w / 2, barH / 2);
            const y = centerY - barH / 2;
            if (typeof ctx.roundRect === "function") {
                ctx.beginPath();
                ctx.roundRect(x, y, w, barH, r);
                ctx.fill();
            }
            else {
                // Фолбэк для старых движков: прямоугольник + скруглённые концы.
                ctx.beginPath();
                ctx.fillRect(x, y + r, w, Math.max(0, barH - 2 * r));
                ctx.arc(x + w / 2, y + r, r, Math.PI, 0);
                ctx.arc(x + w / 2, y + barH - r, r, 0, Math.PI);
                ctx.fill();
            }
        };
        const draw = () => {
            if (disposed || !analyser)
                return;
            rafId = requestAnimationFrame(draw);
            // Ретина-подстройка размеров под фактический бокс.
            const dpr = window.devicePixelRatio || 1;
            const cssW = canvas.clientWidth || 260;
            const cssH = canvas.clientHeight || 56;
            const w = Math.round(cssW * dpr);
            const h = Math.round(cssH * dpr);
            if (canvas.width !== w || canvas.height !== h) {
                canvas.width = w;
                canvas.height = h;
            }
            analyser.getByteFrequencyData(freqData);
            const step = BAR_STEP * dpr;
            const barW = BAR_WIDTH * dpr;
            const barCount = Math.max(6, Math.floor(cssW / BAR_STEP));
            const activeCount = Math.max(1, Math.round(barCount * ACTIVE_RATIO));
            if (heights.length !== barCount)
                heights = new Float32Array(barCount);
            const mid = h / 2;
            const maxBar = h * 0.86;
            const minBar = Math.max(2 * dpr, barW); // минимальная «точка»
            // Полезные бины — нижняя часть спектра, где сосредоточен голос.
            const usableBins = Math.min(freqData.length, 220);
            ctx.clearRect(0, 0, w, h);
            ctx.fillStyle = brand;
            let lastActiveX = step * 0.5;
            for (let i = 0; i < barCount; i += 1) {
                // Envelope: 1 слева → 0 к концу активной зоны (плавное затухание).
                const t = i / activeCount;
                const env = i < activeCount ? Math.pow(1 - t, 1.35) : 0;
                // Значение из спектра, замапленное на активную зону.
                const bin = Math.floor((i / activeCount) * usableBins);
                const raw = i < activeCount ? (freqData[bin] || 0) / 255 : 0;
                const target = env * (0.18 + 0.82 * raw);
                // Плавное сглаживание к цели (~60fps).
                heights[i] += (target - heights[i]) * 0.35;
                const x = step * 0.5 + i * step;
                if (i < activeCount) {
                    const barH = Math.max(minBar, heights[i] * maxBar);
                    ctx.globalAlpha = 0.5 + 0.5 * Math.min(1, env + heights[i]);
                    drawRoundedBar(x, mid, barW, barH);
                    lastActiveX = x + barW;
                }
            }
            // Пунктирная линия «ещё не записано» — от конца активной зоны вправо.
            ctx.globalAlpha = 0.5;
            ctx.strokeStyle = brand;
            ctx.lineWidth = Math.max(1.5, 1.5 * dpr);
            ctx.setLineDash([2 * dpr, 5 * dpr]);
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(Math.min(lastActiveX + step * 0.5, w), mid);
            ctx.lineTo(w - step * 0.5, mid);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
        };
        draw();
        return () => {
            disposed = true;
            if (rafId)
                cancelAnimationFrame(rafId);
            try {
                source?.disconnect();
                analyser?.disconnect();
            }
            catch {
                /* noop */
            }
            // Закрываем контекст (не трогаем сам поток — им владеет рекордер).
            audioCtx?.close().catch(() => { });
        };
    }, [stream]);
    if (!stream)
        return null;
    return (_jsx("div", { className: styles.meter, "aria-hidden": "true", children: _jsx("canvas", { ref: canvasRef, className: styles.canvas }) }));
};
